import type { ReactNode } from "react";
import StatusBadge from "../SurveyTable/StatusBadge";
import { ParticipantCountQueryOptions, getSurveyOptions } from "@pulse/api/queryApi";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, Skeleton } from "frontend-components";
import type { SurveyCreator } from "@pulse/api/surveyApi";

function StatusBox({ title, children }: { title: string; children: ReactNode }) {
    return (
        <Card size="sm" className="col-span-1 shadow-sm">
            <CardHeader className="text-sm uppercase">{title}</CardHeader>
            <CardContent className="pt-0">
                <div className="min-w-0 truncate">{children}</div>
            </CardContent>
        </Card>
    );
}

function TotalResponsesBox({ surveyId }: { surveyId: number }) {
    const { data: count, isLoading } = useQuery(ParticipantCountQueryOptions({ surveyId }));
    const { t } = useTranslation();
    return (
        <StatusBox title={t("responseOverview.totalResponses")}>
            {isLoading ? (
                <Skeleton className="bg-gray-300 w-20 h-8 rounded-full" />
            ) : (
                <span className="text-2xl font-bold">{count?.answered ?? 0}</span>
            )}
        </StatusBox>
    );
}

function AnswerRateBox({ surveyId }: { surveyId: number }) {
    const { data: count, isLoading } = useQuery(ParticipantCountQueryOptions({ surveyId }));
    const { t } = useTranslation();
    const rate = count && count.total > 0 ? Math.round((count.answered / count.total) * 100) : 0;
    return (
        <StatusBox title={t("responseOverview.responseRate")}>
            {isLoading ? (
                <Skeleton className="bg-gray-300 w-20 h-8 rounded-full" />
            ) : (
                <span className="text-2xl font-bold">{rate}%</span>
            )}
        </StatusBox>
    );
}

function CreatedByBox({ createdBy }: { createdBy?: SurveyCreator }) {
    const { t } = useTranslation();
    const name = createdBy
        ? `${createdBy.firstName} ${createdBy.lastName}`
        : t("common.unknownUser");
    return (
        <StatusBox title={t("common.createdBy")}>
            <span className="text-xl" title={name}>
                {name}
            </span>
        </StatusBox>
    );
}

function SentOutBox({ surveyId }: { surveyId: number }) {
    const { data: survey, isLoading } = useQuery(getSurveyOptions({ surveyId }));
    const { i18n, t } = useTranslation();
    const sentOut = survey?.startedAt ? new Date(survey.startedAt) : null;
    return (
        <StatusBox title={t("responseOverview.sentOut")}>
            {isLoading ? (
                <Skeleton className="bg-gray-300 w-20 h-8 rounded-full" />
            ) : (
                <span
                    className="text-xl"
                    title={
                        sentOut?.toLocaleDateString(i18n.language, {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                        }) ?? undefined
                    }
                >
                    {sentOut
                        ? sentOut.toLocaleDateString(i18n.language, {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                          })
                        : "-"}
                </span>
            )}
        </StatusBox>
    );
}

function StatusBadgeBox({ surveyId }: { surveyId: number }) {
    const { data: survey, isLoading } = useQuery(getSurveyOptions({ surveyId }));
    const { t } = useTranslation();
    return (
        <StatusBox title={t("responseOverview.status")}>
            {isLoading || !survey ? (
                <Skeleton className="bg-gray-300 w-20 h-8 rounded-full" />
            ) : (
                <StatusBadge status={survey.status} />
            )}
        </StatusBox>
    );
}

export { TotalResponsesBox, AnswerRateBox, CreatedByBox, SentOutBox, StatusBadgeBox, StatusBox };
